import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, RotateCw, Inbox, Lock, PlugZap } from 'lucide-react';
import type { ApiError } from '../lib/api';
import type { QueryResult } from '../lib/query';
import { useSharedText, type SharedText } from '../lib/i18n';
import { HelpTip } from './Page';

export function Spinner({ size = 16, label }: { size?: number; label?: string }) {
  return (
    <>
      <Loader2 size={size} className="spin" aria-hidden="true" />
      {label ? <span className="visually-hidden">{label}</span> : null}
    </>
  );
}

export function Loading({ label }: { label?: string }) {
  const text = useSharedText();
  return (
    <div className="loading" role="status" aria-live="polite">
      <Loader2 size={18} className="spin" aria-hidden="true" />
      <span>{label ?? text.loading}</span>
    </div>
  );
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="stack stack--sm" aria-hidden="true" style={{ padding: 20 }}>
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="skeleton" style={{ height: 18, width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

function describe(error: ApiError, text: SharedText['errors']): { title: string; body: string; icon: ReactNode } {
  if (error.status === 0) {
    return {
      title: text.unreachable,
      body: error.message,
      icon: <PlugZap size={20} aria-hidden="true" />,
    };
  }
  if (error.status === 403) {
    return { title: text.forbidden, body: error.message, icon: <Lock size={20} aria-hidden="true" /> };
  }
  if (error.status === 404) {
    return {
      title: text.notFound,
      body: error.message,
      icon: <AlertTriangle size={20} aria-hidden="true" />,
    };
  }
  return { title: text.generic, body: error.message, icon: <AlertTriangle size={20} aria-hidden="true" /> };
}

export function ErrorState({ error, onRetry, retrying, context }: { error: ApiError; onRetry?: () => void; retrying?: boolean; context?: string }) {
  const text = useSharedText();
  const info = describe(error, text.errors);
  return (
    <div className="state state--error" role="alert">
      <div className="state__icon">{info.icon}</div>
      <h3>{context ? `${info.title}: ${context}` : info.title}</h3>
      <p>{info.body}</p>
      <span className="state__code">
        {error.status ? `HTTP ${error.status}` : text.errors.network} · {error.code}
      </span>
      {onRetry ? (
        <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry} disabled={retrying}>
          {retrying ? <Spinner size={14} /> : <RotateCw size={14} aria-hidden="true" />}
          {text.tryAgain}
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
  icon,
  center,
  compact,
  tip,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  center?: boolean;
  /** One row: icon, title, optional InfoTip and action (no paragraph). */
  compact?: boolean;
  /** Explanation in an InfoTip beside the title. */
  tip?: ReactNode;
}) {
  if (compact) {
    return (
      <div className="state state--compact" role="status">
        <span className="state__icon" aria-hidden="true">
          {icon ?? <Inbox size={16} aria-hidden="true" />}
        </span>
        <span className="state__title">
          {title}
          <HelpTip help={tip} topic={title} />
        </span>
        {children ? <span className="state__line">{children}</span> : null}
        {action ? <span className="state__action">{action}</span> : null}
      </div>
    );
  }
  return (
    <div className={center ? 'state state--center' : 'state'}>
      <div className="state__icon">{icon ?? <Inbox size={20} aria-hidden="true" />}</div>
      {tip ? (
        <div className="title-row">
          <h3>{title}</h3>
          <HelpTip help={tip} topic={title} />
        </div>
      ) : (
        <h3>{title}</h3>
      )}
      {children ? <p>{children}</p> : null}
      {action ? <div className="row">{action}</div> : null}
    </div>
  );
}

/**
 * Renders loading / error / data for a query. Data rendering only happens with
 * a real response; errors are never replaced by placeholder content.
 */
export function QueryState<T>({
  query,
  children,
  loading,
  context,
}: {
  query: QueryResult<T>;
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  context?: string;
}) {
  const text = useSharedText();
  if (query.data !== undefined) {
    return (
      <>
        {query.error ? (
          <div className="callout callout--warning" role="status" style={{ marginBottom: 12 }}>
            <AlertTriangle size={16} aria-hidden="true" />
            <span>
              {text.staleData(query.error.message)}{' '}
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void query.refetch()}>
                {text.retry}
              </button>
            </span>
          </div>
        ) : null}
        {children(query.data)}
      </>
    );
  }
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.fetching} context={context} />;
  return <>{loading ?? <Loading />}</>;
}
